import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { Download, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { cellRef } from '@/utils/fileHighlights';
import { flowContentBoxWidth } from '@/utils/flowPreviewZoom';
import { escapePreviewHtml } from '@/utils/previewText';

export type FilePreviewStageState =
  | { type: 'loading'; message: string | null }
  | { type: 'error'; message: string; onDownload?: () => void }
  | {
      type: 'fallback';
      message: string;
      canOpen: boolean;
      onOpen: () => void;
      onDownload: () => void;
    }
  | { type: 'image'; url: string; fit: boolean; zoom: number }
  | { type: 'video'; url: string }
  | { type: 'text'; text: string; truncated: boolean }
  | { type: 'html'; html: string }
  | {
      type: 'table';
      rows: string[][];
      note: string | null;
      sheetName: string | null;
      excelRows: number[] | null;
    };

interface FilePreviewStageProps {
  stage: FilePreviewStageState;
  filename: string;
  onImageError: () => void;
  htmlRef?: Ref<HTMLDivElement>;
  /** Zoom for text, Word, and tables. Omitted stages stay at 100%. */
  contentZoom?: number;
  onContentFrame?: () => void;
}

type FlowZoomMode = 'wrap' | 'content';

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  (ref as { current: T | null }).current = value;
}

/**
 * Scales flow content without reflowing it away from the pane width.
 * Spreadsheets (`content`) keep their intrinsic table width so Tilpas bredde
 * can fit that width; text and Word (`wrap`) lay out at the pane width.
 */
function FlowZoomFrame({
  zoom,
  mode,
  layoutKey,
  frameRef,
  onFrame,
  html,
  children,
  ...rest
}: {
  zoom: number;
  mode: FlowZoomMode;
  layoutKey: string;
  frameRef?: Ref<HTMLDivElement>;
  onFrame?: () => void;
  html?: string;
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'children' | 'dangerouslySetInnerHTML'>) {
  const spacerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const frameRefLatest = useRef(frameRef);
  const onFrameRef = useRef(onFrame);
  frameRefLatest.current = frameRef;
  onFrameRef.current = onFrame;
  const [frame, setFrame] = useState<{ width: number; height: number } | null>(null);

  const setInner = useCallback((node: HTMLDivElement | null) => {
    innerRef.current = node;
    assignRef(frameRefLatest.current, node);
  }, []);

  useLayoutEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    const measure = () => {
      const host = inner.closest('.fp-text-view, .fp-docx, .fp-sheet');
      if (!(host instanceof HTMLElement)) return;
      const contentBox = flowContentBoxWidth(host);
      let width = 0;
      if (mode === 'content' && contentBox > 1) {
        const table = inner.querySelector('table');
        const intrinsic = table instanceof HTMLElement ? table.scrollWidth : inner.scrollWidth;
        width = Math.max(contentBox, intrinsic);
      }
      const height = inner.offsetHeight;
      if (width <= 1 && height <= 1) return;
      setFrame((current) => {
        if (
          current &&
          Math.abs(current.width - width) <= 1 &&
          Math.abs(current.height - height) <= 1
        ) {
          return current;
        }
        return { width, height };
      });
    };

    measure();
    const host = inner.closest('.fp-text-view, .fp-docx, .fp-sheet');
    if (!(host instanceof Element) || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(host);
    return () => observer.disconnect();
  }, [layoutKey, mode, frame?.width]);

  useLayoutEffect(() => {
    onFrameRef.current?.();
  }, [frame, zoom]);

  const spacerStyle: CSSProperties = { ['--fp-zoom' as string]: String(zoom) };
  const innerStyle: CSSProperties = {};
  if (frame && frame.height > 1) spacerStyle.height = Math.round(frame.height * zoom);
  if (mode === 'content' && frame && frame.width > 1) {
    spacerStyle.width = Math.round(frame.width * zoom);
    innerStyle.width = Math.round(frame.width);
  }

  const body =
    html != null ? (
      <div
        ref={setInner}
        className="fp-flow-zoom"
        style={innerStyle}
        {...rest}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    ) : (
      <div ref={setInner} className="fp-flow-zoom" style={innerStyle} {...rest}>
        {children}
      </div>
    );

  return (
    <div ref={spacerRef} className="fp-flow-spacer" style={spacerStyle} data-fp-zoom={String(zoom)}>
      {body}
    </div>
  );
}

export function FilePreviewStage({
  stage,
  filename,
  onImageError,
  htmlRef,
  contentZoom,
  onContentFrame,
}: FilePreviewStageProps) {
  const { t } = useTranslation();

  switch (stage.type) {
    case 'loading':
      return (
        <div className="fp-fallback">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-ds-muted" />
            {stage.message ? <p className="text-sm text-ds-muted">{stage.message}</p> : null}
          </div>
        </div>
      );
    case 'error':
      return (
        <div className="fp-fallback">
          <div className="flex max-w-md flex-col items-center gap-4 px-6 text-center">
            <p className="fp-fallback-title">{stage.message}</p>
            {stage.onDownload ? (
              <Button type="button" variant="secondary" onClick={stage.onDownload}>
                <Download />
                {t('documents.download')}
              </Button>
            ) : null}
          </div>
        </div>
      );
    case 'fallback':
      return (
        <div className="fp-fallback">
          <div className="flex max-w-md flex-col items-center gap-4 px-6 text-center">
            <FileText className="h-12 w-12 text-ds-muted" aria-hidden />
            <p className="fp-fallback-title">{stage.message}</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {stage.canOpen ? (
                <Button type="button" variant="secondary" onClick={stage.onOpen}>
                  <ExternalLink />
                  {t('documents.open_in_new_tab')}
                </Button>
              ) : null}
              <Button type="button" variant="secondary" onClick={stage.onDownload}>
                <Download />
                {t('documents.highlights.download')}
              </Button>
            </div>
          </div>
        </div>
      );
    case 'image':
      return (
        <div className={stage.fit ? 'fp-media is-fit' : 'fp-media is-zoomed'}>
          <img
            src={stage.url}
            alt={filename}
            style={stage.fit ? undefined : { width: `${stage.zoom * 100}%` }}
            onError={onImageError}
          />
        </div>
      );
    case 'video':
      return (
        <div className="fp-media is-fit">
          <video src={stage.url} controls />
        </div>
      );
    case 'text':
      return (
        <div className="fp-text-view">
          {stage.truncated ? (
            <p className="fp-sheet-note">{t('documents.preview.truncated')}</p>
          ) : null}
          <FlowZoomFrame
            zoom={contentZoom ?? 1}
            mode="wrap"
            layoutKey={stage.text}
            frameRef={htmlRef}
            onFrame={onContentFrame}
            data-fp-page="1"
            data-fp-text="html"
          >
            <pre dangerouslySetInnerHTML={{ __html: escapePreviewHtml(stage.text) }} />
          </FlowZoomFrame>
        </div>
      );
    case 'html':
      return (
        <div className="fp-docx">
          <FlowZoomFrame
            zoom={contentZoom ?? 1}
            mode="wrap"
            layoutKey={stage.html}
            frameRef={htmlRef}
            onFrame={onContentFrame}
            html={stage.html}
            data-fp-page="1"
            data-fp-text="html"
          />
        </div>
      );
    case 'table': {
      const highlightable = stage.sheetName != null;
      return (
        <div className="fp-sheet">
          {stage.note ? <p className="fp-sheet-note">{stage.note}</p> : null}
          <FlowZoomFrame
            zoom={contentZoom ?? 1}
            mode="content"
            layoutKey={`${stage.sheetName ?? ''}:${stage.rows.length}:${stage.note ?? ''}`}
            frameRef={htmlRef}
            onFrame={onContentFrame}
            data-fp-page="1"
            data-fp-text={highlightable ? 'sheet' : undefined}
            data-fp-sheet={highlightable ? (stage.sheetName ?? undefined) : undefined}
          >
            {stage.rows.length === 0 ? (
              <p className="fp-fallback-title">{t('documents.preview.empty_sheet')}</p>
            ) : (
              <table>
                <tbody>
                  {stage.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((value, columnIndex) => {
                        const excelRow = stage.excelRows?.[rowIndex] ?? rowIndex + 1;
                        return highlightable ? (
                          <td
                            key={columnIndex}
                            data-cell={cellRef(columnIndex, excelRow)}
                            data-fp-col={columnIndex}
                            data-fp-row={excelRow}
                          >
                            {value}
                          </td>
                        ) : (
                          <td key={columnIndex}>{value}</td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </FlowZoomFrame>
        </div>
      );
    }
    default: {
      const exhaustive: never = stage;
      return exhaustive;
    }
  }
}
